import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Input, Modal, Select, Spin, message } from 'antd';
import { useTranslation } from '../../../i18n';
import { useCurrentAccount } from '../layout/currentAccountContext';
import { useSelectedShop } from '../layout/selectedShopContext';
import { rememberDmConversation } from '../../../store/dmConversations';
import {
  createDmConversation,
  createEmailCampaign,
  getContentTemplates,
  getInfluencerCollectionPage,
  getInfluencerPage,
} from '../../../store/request';
import type { InfluencerCollectionRecord, InfluencerRecord } from '../../../types/influencer';
import type { TemplateListItem } from '../../../types/template';

type TalentTab = 'all' | 'mine' | 'square' | 'paste';

type OutreachTalentSource = {
  influencerId?: string;
  creatorOpenId?: string;
  nickname?: string;
  username?: string;
  email?: string;
  avatarUrl?: string;
  selectionRegion?: string;
  categoryNames?: string[];
};

type OutreachChannel = 'platform_email' | 'private_message';

type QuickOutreachModalProps = {
  open: boolean;
  presetTalentIds?: string[];
  /** 打开弹窗时已勾选达人的展示资料。未收藏的达人不在「我的达人」里，靠这里在「全部」中显示。 */
  presetTalents?: OutreachTalentSource[];
  /** 打开时预选渠道：邮件 / 私信。 */
  presetChannel?: OutreachChannel;
  onClose: () => void;
};

type PickerTalent = {
  id: string;
  influencerId: string;
  creatorOpenId: string;
  name: string;
  handle: string;
  email: string;
  avatarUrl: string;
  initial: string;
  color: string;
  meta: string;
};

function toPickerTalent(row: OutreachTalentSource): PickerTalent | null {
  const influencerId = String(row.influencerId ?? '').trim();
  const creatorOpenId = String(row.creatorOpenId ?? '').trim();
  const id = influencerId || creatorOpenId;
  if (!id) return null;
  const name = row.nickname || row.username || id;
  const handle = row.username?.replace(/^@/, '').trim() || '';
  const email = row.email?.trim() || '';
  const categories = row.categoryNames?.filter(Boolean).slice(0, 2).join(' · ') ?? '';
  return {
    id,
    influencerId,
    creatorOpenId,
    name,
    handle,
    email,
    avatarUrl: row.avatarUrl?.trim() || '',
    initial: name.slice(0, 1).toUpperCase() || '?',
    color: '#596ae8',
    meta: [handle ? `@${handle}` : '', row.selectionRegion, categories].filter(Boolean).join(' · ') || '—',
  };
}

export function QuickOutreachModal({
  open,
  presetTalentIds,
  presetTalents,
  presetChannel = 'platform_email',
  onClose,
}: QuickOutreachModalProps) {
  const { t } = useTranslation();
  const account = useCurrentAccount();
  const { shopId } = useSelectedShop();
  const [channel, setChannel] = useState<OutreachChannel>(presetChannel);
  const [taskName, setTaskName] = useState('');
  const [templateId, setTemplateId] = useState<number>();
  const [templates, setTemplates] = useState<TemplateListItem[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState<TalentTab>('mine');
  const [keyword, setKeyword] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [pasteText, setPasteText] = useState('');
  const [mineRecords, setMineRecords] = useState<InfluencerCollectionRecord[]>([]);
  const [mineTotal, setMineTotal] = useState(0);
  const [mineLoading, setMineLoading] = useState(false);
  const [squareRecords, setSquareRecords] = useState<InfluencerRecord[]>([]);
  const [squareLoading, setSquareLoading] = useState(false);
  const [pickedTalents, setPickedTalents] = useState<PickerTalent[]>([]);

  const channelOptions = useMemo(
    () => [
      { value: 'platform_email', label: t('invitation.channel_email') },
      { value: 'private_message', label: t('invitation.channel_dm') },
    ],
    [t],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    const fromRows = (presetTalents ?? []).map(toPickerTalent).filter((item): item is PickerTalent => item != null);
    const ids = Array.from(
      new Set([
        ...fromRows.map((item) => item.id),
        ...(presetTalentIds ?? []).map((id) => id.trim()).filter(Boolean),
      ]),
    );
    setSelectedIds(ids);
    setPickedTalents(fromRows);
    setChannel(presetChannel);
    setSquareRecords([]);
    setTab('mine');
    setKeyword('');
    setPasteText('');
    setTaskName('');
    setTemplateId(undefined);
  }, [open, presetTalentIds, presetTalents, presetChannel]);

  const mineKeyword = tab === 'mine' ? keyword.trim() : '';
  const squareKeyword = tab === 'square' ? keyword.trim() : '';

  useEffect(() => {
    if (!open) return;
    const userCode = account?.userCode?.trim();
    if (!userCode) {
      setMineRecords([]);
      setMineTotal(0);
      setMineLoading(false);
      return;
    }
    let cancelled = false;
    setMineLoading(true);
    const timer = window.setTimeout(() => {
      const loadAll = async () => {
        const pageSize = 50;
        const all: InfluencerCollectionRecord[] = [];
        let pageNum = 1;
        let total = 0;
        while (pageNum <= 40) {
          const res = await getInfluencerCollectionPage({
            userCode,
            pageNum,
            pageSize,
            ...(mineKeyword ? { keyword: mineKeyword } : {}),
          });
          if (cancelled) return;
          if (res.code !== 200) {
            message.error(res.message || t('creator.load_failed'));
            setMineRecords([]);
            setMineTotal(0);
            return;
          }
          const page = res.content;
          const records = page?.records;
          const list = Array.isArray(records) ? records : [];
          total = typeof page?.total === 'number' ? page.total : all.length + list.length;
          all.push(...list);
          if (list.length === 0 || all.length >= total) break;
          pageNum += 1;
        }
        if (cancelled) return;
        setMineRecords(all);
        setMineTotal(total);
      };
      loadAll()
        .catch(() => {
          if (!cancelled) {
            message.error(t('creator.load_failed'));
            setMineRecords([]);
            setMineTotal(0);
          }
        })
        .finally(() => {
          if (!cancelled) setMineLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, account?.userCode, mineKeyword, t]);

  useEffect(() => {
    if (!open || tab !== 'square') return;
    let cancelled = false;
    setSquareLoading(true);
    const timer = window.setTimeout(() => {
      getInfluencerPage({
        pageNum: 1,
        pageSize: 50,
        ...(squareKeyword ? { keyword: squareKeyword } : {}),
      })
        .then((res) => {
          if (cancelled) return;
          if (res.code !== 200) {
            message.error(res.message || t('talent.load_failed'));
            setSquareRecords([]);
            return;
          }
          const list = res.content?.records;
          setSquareRecords(Array.isArray(list) ? list : []);
        })
        .catch(() => {
          if (!cancelled) message.error(t('talent.load_failed'));
        })
        .finally(() => {
          if (!cancelled) setSquareLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, tab, squareKeyword, t]);

  const emailChannel = channel === 'platform_email';
  const templateChannel = emailChannel ? 'EMAIL' : 'DM';

  useEffect(() => {
    if (!open) return;
    const currentShopId = shopId?.trim();
    if (!currentShopId) {
      setTemplates([]);
      setTemplatesLoading(false);
      return;
    }
    let cancelled = false;
    setTemplatesLoading(true);
    setTemplateId(undefined);
    getContentTemplates({ shopId: currentShopId, channel: templateChannel, pageNum: 1, pageSize: 100 })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          message.error(res.message || t('invitation.template_load_failed'));
          setTemplates([]);
          return;
        }
        const list = res.content?.records;
        setTemplates(Array.isArray(list) ? list.filter((item) => item.id != null) : []);
      })
      .catch(() => {
        if (!cancelled) {
          message.error(t('invitation.template_load_failed'));
          setTemplates([]);
        }
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, templateChannel, shopId, t]);

  const mineTalents = useMemo(
    () => mineRecords.map(toPickerTalent).filter((item): item is PickerTalent => item != null),
    [mineRecords],
  );

  const squareTalents = useMemo(
    () => squareRecords.map(toPickerTalent).filter((item): item is PickerTalent => item != null),
    [squareRecords],
  );

  const talentById = useMemo(() => {
    const map = new Map<string, PickerTalent>();
    pickedTalents.forEach((item) => map.set(item.id, item));
    mineTalents.forEach((item) => map.set(item.id, item));
    squareTalents.forEach((item) => map.set(item.id, item));
    return map;
  }, [pickedTalents, mineTalents, squareTalents]);

  const listSource = useMemo(() => {
    if (tab === 'mine') return mineTalents;
    if (tab === 'square') return squareTalents;
    if (tab === 'all') {
      return selectedIds
        .map((id) => talentById.get(id))
        .filter((item): item is PickerTalent => item != null);
    }
    return [];
  }, [tab, selectedIds, mineTalents, squareTalents, talentById]);

  const toggleTalent = (talent: PickerTalent, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? (prev.includes(talent.id) ? prev : [...prev, talent.id]) : prev.filter((id) => id !== talent.id),
    );
    setPickedTalents((prev) =>
      checked
        ? prev.some((item) => item.id === talent.id)
          ? prev
          : [...prev, talent]
        : prev.filter((item) => item.id !== talent.id),
    );
  };

  const selectAllVisible = () => {
    const ids = listSource.map((talent) => talent.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
      setPickedTalents((prev) => prev.filter((item) => !ids.includes(item.id)));
      return;
    }
    setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
    setPickedTalents((prev) => {
      const map = new Map(prev.map((item) => [item.id, item]));
      listSource.forEach((item) => map.set(item.id, item));
      return Array.from(map.values());
    });
  };

  const submit = async () => {
    if (submitting) return;
    if (selectedIds.length === 0) {
      message.warning(t('invitation.pick_talent'));
      return;
    }
    const currentShopId = shopId?.trim();
    if (!emailChannel) {
      if (!currentShopId) {
        message.warning(t('invitation.invite_shop_required'));
        return;
      }
      const creatorOpenIds = selectedIds
        .map((id) => {
          const talent = talentById.get(id);
          return (talent?.creatorOpenId || talent?.id || id).trim();
        })
        .filter(Boolean);
      if (creatorOpenIds.length === 0) {
        message.warning(t('invitation.pick_talent'));
        return;
      }
      setSubmitting(true);
      try {
        let created = 0;
        for (const creatorOpenId of creatorOpenIds) {
          const res = await createDmConversation({ shopId: currentShopId, creatorOpenId });
          if (res.code !== 200) {
            if (res.code !== -1) message.error(res.message || t('invitation.dm_failed'));
            return;
          }
          const conversationId = res.content?.platformConversationId?.trim();
          if (!conversationId) {
            message.error(res.message || t('invitation.dm_missing_conversation'));
            return;
          }
          rememberDmConversation(currentShopId, creatorOpenId, conversationId);
          created += 1;
          if (creatorOpenIds.length === 1) {
            message.success(res.message || t('invitation.dm_created'));
          }
        }
        if (created > 1) message.success(t('invitation.dm_created'));
        onClose();
      } finally {
        setSubmitting(false);
      }
      return;
    }
    if (!currentShopId) {
      message.warning(t('invitation.invite_shop_required'));
      return;
    }
    if (templateId == null) {
      message.warning(t('invitation.template_placeholder'));
      return;
    }
    const template = templates.find((item) => item.id === templateId);
    const subjectText = template?.name?.trim() || '—';
    const body = template?.body?.trim() || '';
    const chosen = selectedIds
      .map((id) => talentById.get(id))
      .filter((item): item is PickerTalent => item != null);
    const recipients = chosen
      .filter((item) => item.email && item.influencerId)
      .map((item) => ({
        influencerId: item.influencerId,
        email: item.email,
        ...(item.name.trim() ? { name: item.name.trim() } : {}),
        ...(item.handle ? { handle: item.handle } : {}),
      }));
    if (recipients.length === 0) {
      message.warning(t('invitation.campaign_no_email'));
      return;
    }
    const skipped = chosen.length - recipients.length;
    if (skipped > 0) {
      message.warning(t('invitation.campaign_skipped_email', { count: skipped }));
    }
    const name = taskName.trim();
    setSubmitting(true);
    try {
      const res = await createEmailCampaign({
        shopId: currentShopId,
        subject: subjectText,
        ...(body ? { bodyText: body } : {}),
        recipients,
        ...(name ? { name: name.slice(0, 255) } : {}),
        templateId,
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('invitation.campaign_failed'));
        return;
      }
      message.success(res.message || t('invitation.campaign_created'));
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const tabBtnClass = (active: boolean) =>
    active
      ? 'rounded-full border-0 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700'
      : 'cursor-pointer rounded-full border border-[#eef0f4] bg-white px-3 py-1 text-xs text-slate-600 hover:border-[#596ae8]/30';

  return (
    <Modal
      title={t('invitation.outreach_title')}
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={560}
      styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto', paddingTop: 12 } }}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="primary" className="wb-btn-primary" loading={submitting} onClick={submit}>
            {t('invitation.create_task')}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <div>
          <div className="mb-1.5 text-sm font-medium text-slate-700">{t('invitation.channel')}</div>
          <Select className="w-full" value={channel} options={channelOptions} onChange={setChannel} />
        </div>
        {emailChannel ? (
          <div>
            <div className="mb-1.5 text-sm font-medium text-slate-700">{t('invitation.task_name')}</div>
            <Input
              allowClear
              maxLength={255}
              placeholder={t('invitation.task_name_placeholder')}
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
            />
          </div>
        ) : null}
        <div>
          <div className="mb-1.5 text-sm font-medium text-slate-700">{t('invitation.script_or_invite')}</div>
          <Select
            allowClear
            className="w-full"
            placeholder={t('invitation.template_placeholder')}
            loading={templatesLoading}
            value={templateId}
            options={templates
              .filter((item) => item.id != null)
              .map((item) => ({ value: item.id as number, label: item.name?.trim() || String(item.id) }))}
            onChange={(value) => setTemplateId(value)}
          />
        </div>
        <div>
          <div className="mb-1 text-sm font-medium text-slate-700">{t('invitation.select_talent')}</div>
          <p className="mb-2 mt-0 text-xs leading-relaxed text-slate-400">
            {selectedIds.length === 0
              ? t('invitation.none_selected')
              : t('invitation.selected_count', { count: selectedIds.length })}
          </p>
          <Input
            allowClear
            placeholder={t('invitation.search_placeholder')}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" className={tabBtnClass(tab === 'all')} onClick={() => setTab('all')}>
              {t('invitation.all_count', { count: selectedIds.length })}
            </button>
            <button type="button" className={tabBtnClass(tab === 'mine')} onClick={() => setTab('mine')}>
              {t('invitation.my_talents', { count: mineTalents.length })}
            </button>
            <button type="button" className={tabBtnClass(tab === 'square')} onClick={() => setTab('square')}>
              {t('invitation.search_square')}
            </button>
            {/* <button type="button" className={tabBtnClass(tab === 'paste')} onClick={() => setTab('paste')}>
              {t('invitation.paste_list')}
            </button>
            <button type="button" className={tabBtnClass(false)} onClick={selectAllVisible}>
              {t('invitation.select_all')}
            </button> */}
          </div>
          {tab === 'paste' ? (
            <Input.TextArea
              className="mt-3"
              rows={3}
              placeholder={t('invitation.paste_placeholder')}
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
            />
          ) : (
            <div
              className={`mt-3 overflow-y-auto rounded-lg border border-[#eef0f4] bg-[#fafbfc] px-2 py-1 ${emailChannel ? 'max-h-[120px]' : 'max-h-[220px]'}`}
            >
              {(tab === 'mine' && mineLoading) || (tab === 'square' && squareLoading) ? (
                <div className="flex justify-center py-6">
                  <Spin size="small" />
                </div>
              ) : null}
              {listSource.map((talent) => (
                <label
                  key={talent.id}
                  className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2.5 hover:bg-white"
                >
                  <Checkbox
                    checked={selectedIds.includes(talent.id)}
                    onChange={(e) => toggleTalent(talent, e.target.checked)}
                  />
                  {talent.avatarUrl ? (
                    <img
                      src={talent.avatarUrl}
                      alt=""
                      className="size-8 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <span
                      className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                      style={{ backgroundColor: talent.color }}
                    >
                      {talent.initial}
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-slate-800">{talent.name}</span>
                    <span className="block truncate text-xs text-slate-500">{talent.meta}</span>
                  </span>
                </label>
              ))}
              {((tab !== 'mine' && tab !== 'square') ||
                (tab === 'mine' && !mineLoading) ||
                (tab === 'square' && !squareLoading)) &&
                listSource.length === 0 ? (
                <p className="px-2 py-6 text-center text-sm text-slate-400">
                  {tab === 'all' ? t('invitation.empty_selected') : t('invitation.no_match')}
                </p>
              ) : null}
            </div>
          )}
        </div>

      </div>
    </Modal>
  );
}
